import type { DashboardVM } from '../../lib/compute';
import { SpendMixChart } from '../charts';
import { KpiCard } from '../ui';

export function Werbung({ vm, adCountry, onAdCountry }: { vm: DashboardVM; adCountry: string | null; onAdCountry: (c: string | null) => void }) {
  return (
    <>
      <div className="cw-pagehead">
        <h1>Werbung &amp; ROAS</h1>
        <span className="meta">Spend nach Land (Meta + Google + Amazon Ads) · {vm.rangeCaption}</span>
      </div>

      {vm.amazonAdsSpendPending && (
        <div className="cw-roas-note warn">
          Amazon-Ads-Werbekosten noch nicht angebunden — ROAS für Länder mit hohem Amazon-Anteil ist daher aktuell überzeichnet.
        </div>
      )}

      {vm.channelsFiltered && (
        <div className="cw-roas-note">
          Kanal-Auswahl aktiv — Werbekosten & ROAS nach Kanal-Attribution: Amazon Ads → Amazon, OTTO Ads → OTTO, Meta/Google → Webshops (cosyworld.com + WooCommerce).
        </div>
      )}

      {vm.adChannels.selectedCountry && (
        <div className="cw-scope-chip">
          <span>Gefiltert auf <strong>{vm.adChannels.selectedCountry}</strong> — alle Kennzahlen dieser Seite</span>
          <button className="cw-link-reset" onClick={() => onAdCountry(null)}>Alle Länder anzeigen</button>
        </div>
      )}

      <div className="cw-kpi3">
        {vm.adsKpis.map((k) => (
          <KpiCard key={k.label} kpi={k} large />
        ))}
      </div>

      <section className="cw-card" style={{ padding: '22px 24px 26px', marginBottom: 20 }}>
        <div className="cw-card-head">
          <h2>ROAS Zielband</h2>
          <span className="meta">Ziel 3,5–4,1× · aktuell <span className="cw-num" style={{ fontWeight: 700, color: '#2B3136' }}>{vm.adsRoas}</span>{vm.adChannels.selectedCountry ? ` · ${vm.adChannels.selectedCountry}` : ''}</span>
        </div>
        <div className="cw-gauge-wrap">
          <div className="cw-gauge-band" style={{ left: vm.gauge.bandLeft, width: vm.gauge.bandWidth }} />
          {vm.gauge.hasRoas && <div className="cw-gauge-marker" style={{ left: vm.gauge.markerLeft }} />}
          {vm.gauge.hasRoas && <div className="cw-gauge-marker-lbl cw-num" style={{ left: vm.gauge.markerLeft }}>{vm.adsRoas}</div>}
        </div>
        <div className="cw-gauge-ticks">
          {vm.gauge.ticks.map((t) => (
            <span key={t.label} className="cw-gauge-tick cw-num" style={{ left: t.left }}>{t.label}</span>
          ))}
        </div>
      </section>

      <div className="cw-ads-grid">
        <section className="cw-card" style={{ padding: '22px 24px' }}>
          <div className="cw-card-head" style={{ marginBottom: 4 }}>
            <h2>Werbekosten pro Land</h2>
            <span className="meta">nach Kanal · Land wählen → Details rechts</span>
          </div>
          <div className="cw-legend" style={{ marginTop: 6 }}>
            {vm.spendByCountryChannels.channels.map((c) => (
              <div key={c.name} className="item"><span className="sq" style={{ background: c.color }} /><span className="lbl">{c.name}</span></div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            {vm.spendByCountryChannels.rows.length === 0 && <div className="cw-team-empty">Keine Daten im gewählten Zeitraum.</div>}
            {vm.spendByCountryChannels.rows.map((r) => (
              <div
                key={r.name}
                className={`cw-roasbar-row is-clickable${adCountry === r.name ? ' is-selected' : ''}`}
                role="button"
                tabIndex={0}
                aria-pressed={adCountry === r.name}
                title={r.segments.map((sg) => `${sg.name} ${sg.value} (${sg.share})`).join(' · ')}
                onClick={() => onAdCountry(r.name)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAdCountry(r.name); } }}
              >
                <span className="cw-roasbar-name">{r.name}</span>
                {r.noSpend ? (
                  <span className="cw-roasbar-pending">kein getrackter Spend</span>
                ) : (
                  <>
                    <div className="cw-roasbar-track cw-stack-track">
                      <div className="cw-stack-bar" style={{ width: r.barW }}>
                        {r.segments.map((sg) => (
                          <div key={sg.name} className="cw-stack-seg" style={{ width: sg.w, background: sg.color }} />
                        ))}
                      </div>
                    </div>
                    <span className="cw-roasbar-val cw-num cw-stack-total">{r.total}</span>
                    <span className="cw-roasbar-roas cw-num" style={{ color: r.roasColor }}>{r.roasLabel}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="cw-card" style={{ padding: '22px 24px' }}>
          <div className="cw-card-head" style={{ marginBottom: 4 }}>
            <h2>Werbekosten nach Kanal</h2>
            <span className="meta">
              {vm.adChannels.scopeLabel} · {vm.adChannels.total}
              {vm.adChannels.selectedCountry && (
                <button className="cw-link-reset" onClick={() => onAdCountry(null)}>Alle Länder</button>
              )}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 10 }}>
            {vm.adChannels.rows.length === 0 && <div className="cw-team-empty">Keine getrackten Werbekosten in der Auswahl.</div>}
            {vm.adChannels.rows.map((r) => (
              <div key={r.name} className="cw-chan-row">
                <div className="cw-chan-top">
                  <div className="cw-chan-name">
                    <span className="dot" style={{ background: r.color }} />
                    <span className="nm">{r.name}</span>
                  </div>
                  <span className={`cw-chan-ums cw-num${r.pending ? ' cw-pending' : ''}`}>{r.pending ? r.value : r.share}</span>
                </div>
                <div className="cw-bar-track">
                  <div className="cw-bar-fill" style={{ width: r.barW, background: r.color }} />
                </div>
                <div className="cw-chan-meta">
                  <span className="s cw-num">{r.pending ? 'Anbindung ausstehend' : r.value}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="cw-card" style={{ padding: '22px 24px 18px' }}>
        <div className="cw-card-head">
          <h2>Werbekosten-Mix nach {vm.periodTitle}{vm.adChannels.selectedCountry ? ` · ${vm.adChannels.selectedCountry}` : ''}</h2>
          <span className="meta">Anteil je Kanal am Spend der Periode (100 %)</span>
        </div>
        <div className="cw-legend">
          {vm.spendMix.channels.map((c) => (
            <div key={c.name} className="item"><span className="sq" style={{ background: c.color }} /><span className="lbl">{c.name}</span></div>
          ))}
        </div>
        <SpendMixChart mix={vm.spendMix} />
        <div className="cw-table-scroll">
          <table className="cw-table">
            <thead>
              <tr>
                <th className="l">{vm.periodHead}</th>
                {vm.spendMix.channels.map((c) => (
                  <th key={c.name} className="r">{c.name}</th>
                ))}
                <th className="r">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {vm.spendMix.table.map((row) => (
                <tr key={row.label}>
                  <td className="l kw">{row.label}</td>
                  {row.cells.map((v, i) => (
                    <td key={i} className="r muted">{v}</td>
                  ))}
                  <td className="r">{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
